import type { TFunction } from 'i18next';
import type { Timelog } from '@/modules/attendance/domain/entities/Timelog';
import type { ThemeTokens, BrandColors } from '@/shared/ui/theme/tokens';
import { Avatar } from '@/presentation/components/Avatar';
import { VerificationIcon } from '@/modules/employees/presentation/components/VerificationIcon';
import { parsePgDateTime } from '@/shared/utils/datetime';
import { capitalizeIfUpper } from '@/shared/utils/mapping';
import { mapDeviceActionTypeToFuente } from '@/modules/attendance/domain/helpers/deviceActionType';
import { resolveVerificationLabels } from '@/modules/attendance/domain/helpers/verificationMode';
import { useIsNarrowViewport } from '@/shared/hooks/useIsNarrowViewport';

function formatHora(datetime: string): string {
  try {
    const d = parsePgDateTime(datetime);
    const h24 = d.getHours();
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ampm = h24 >= 12 ? 'pm' : 'am';
    const h12 = String(h24 % 12 || 12).padStart(2, '0');
    return `${h12}:${mm} ${ampm}`;
  } catch {
    return datetime.slice(11, 16);
  }
}

interface Props {
  tl: Timelog;
  onOpenDetail: (tl: Timelog) => void;
  T: ThemeTokens;
  C: BrandColors;
  t: TFunction;
}

export function AdminMarcajeRow({ tl, onOpenDetail, T, C, t }: Props) {
  const isNarrow = useIsNarrowViewport();
  const isEnt = tl.accessType === 1;
  const tipoColor = isEnt ? C.success : C.danger;
  const tipoLabel = isEnt ? t('timelog.entry') : t('timelog.exit');
  const hora = formatHora(tl.datetime);
  const emp = tl.employee;
  const initials = emp?.initials ?? '??';
  const avatarColor = emp?.avatarColor ?? C.primary;
  const sedeLabel = capitalizeIfUpper(tl.structureCode ?? '—');

  // Resolve method + source labels via canonical lookup table
  const labels = resolveVerificationLabels(tl.verificationMode, tl.source ?? null, t);

  // Source (Fuente) column: use resolveVerificationLabels when verificationMode is present,
  // fall back to mapDeviceActionTypeToFuente for legacy timelogs without a verificationMode.
  const hasVerificationMode = tl.verificationMode != null;
  const fuenteLabel = hasVerificationMode
    ? labels.source
    : mapDeviceActionTypeToFuente(tl.deviceActionType ?? undefined);

  return (
    <div
      onClick={() => { onOpenDetail(tl); }}
      style={{
        display: 'grid',
        gridTemplateColumns: isNarrow
          ? '32px minmax(140px, 1.6fr) minmax(90px, 1fr)'
          : '32px minmax(140px, 1.6fr) minmax(90px, 1fr) minmax(80px, 0.9fr) minmax(80px, 0.9fr)',
        alignItems: 'center',
        gap: 9,
        padding: '11px 15px',
        borderRadius: 11,
        cursor: 'pointer',
        background: T.surfaceBg,
        border: `1px solid ${T.border}`,
        marginBottom: 7,
        transition: 'all .15s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = T.rowBg; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = T.surfaceBg; }}
    >
      {/* Col 1 — Avatar */}
      <Avatar
        data-testid="admin-marcajes-row-avatar"
        initials={initials}
        color={avatarColor}
        size={32}
        photoUrl={tl.capturePhotoUrl ?? tl.employee?.photoUrl ?? null}
      />

      {/* Col 2 — employee name on top + tipo·hora (admin general view) */}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {emp?.fullName ?? '—'}
        </div>
        <div style={{ marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 5, background: `${tipoColor}15`, color: tipoColor, borderRadius: 10, padding: '2px 9px', fontSize: 9.5, fontWeight: 700 }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: tipoColor }} />
          {tipoLabel} · {hora}
        </div>
      </div>

      {/* Col 3 — Sede */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: T.textMuted, minWidth: 0 }}>
        <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0 }}>
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
        </svg>
        <span style={{ color: T.text, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sedeLabel}</span>
      </div>

      {/* Col 4 — Método (hidden on narrow viewports; still available via row-tap detail modal) */}
      {!isNarrow && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: T.text, fontWeight: 500, flexShrink: 0 }}>
          <span style={{ color: T.textMuted, display: 'flex' }}>
            <VerificationIcon iconKey={labels.methodIconKey} />
          </span>
          {labels.method}
        </div>
      )}

      {/* Col 5 — Fuente (hidden on narrow viewports; still available via row-tap detail modal) */}
      {!isNarrow && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: T.text, fontWeight: 500, flexShrink: 0 }}>
          <span style={{ color: T.textMuted, display: 'flex' }}>
            {hasVerificationMode ? (
              <VerificationIcon iconKey={labels.sourceIconKey} />
            ) : (
              <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0 }}>
                <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
              </svg>
            )}
          </span>
          {fuenteLabel}
        </div>
      )}
    </div>
  );
}
